/**
 * useModuleChatRailItem - the app-shell-level AI copilot right-rail item.
 *
 * Makes the AI chat available on EVERY page at initialization without per-page
 * wiring: AppShell merges this item into the page's rightRailItems unless the
 * page provides its own module-chat item (isModuleChat - DealWorkspace) or opts
 * out entirely (suppressModuleChat - module landing pages).
 *
 * Wired identically to DealWorkspace's chat item minus the page-specific
 * pieces (onAction card handlers, generation plan drawer): the chat session is
 * the shared workspace-scoped connection from ChatSessionProvider, so a run
 * started on one page keeps streaming after navigation.
 */

import { useModuleChatSessionSafe } from '../../contexts/ChatSessionContext';
import { usePublicAppConfig } from '../../contexts/PublicAppConfigContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { AiChatPane } from '../../features/ai-chat/components/AiChatPane';
import { ChatConnectionIndicator } from '../../features/ai-chat/components/ChatConnectionIndicator';
import {
  SessionControls,
  SessionTitleEditable,
} from '../../features/ai-chat/components/SessionControls';
import { AiBrandIcon } from '../shared/AiBrandIcon';
import type { RightRailItem } from './AppShell';

export const MODULE_CHAT_RAIL_ID = 'module-chat';

export function useModuleChatRailItem(): RightRailItem | null {
  const chatSession = useModuleChatSessionSafe();
  const { workspace } = useWorkspace();
  const { config: appConfig } = usePublicAppConfig();

  if (!chatSession || !workspace?.uuid) return null;

  return {
    id: MODULE_CHAT_RAIL_ID,
    isModuleChat: true,
    label: appConfig.aiAssistantName ?? 'AI',
    icon: <AiBrandIcon className="deal-page__ai-icon deal-page__ai-icon--md" />,
    noPadding: true,
    headerTitle: (
      <SessionTitleEditable
        sessions={chatSession.state.sessions}
        activeSessionUuid={chatSession.state.activeSessionUuid}
        onRename={chatSession.renameSession}
      />
    ),
    headerAction: (
      <div className="chat-header-action">
        <ChatConnectionIndicator
          connectionStatus={chatSession.state.connectionStatus}
          reconnectAttempt={chatSession.state.reconnectAttempt}
          onReconnect={chatSession.manualReconnect}
        />
        <SessionControls
          sessions={chatSession.state.sessions}
          sessionsStatus={chatSession.state.sessionsStatus}
          activeSessionUuid={chatSession.state.activeSessionUuid}
          sessionPool={chatSession.state.sessionPool}
          onLoad={chatSession.loadSessions}
          onSwitch={chatSession.switchSession}
          onCreate={chatSession.createNewSession}
        />
      </div>
    ),
    content: (
      <AiChatPane
        state={chatSession.state}
        isConnected={chatSession.isConnected}
        isRunActive={chatSession.isRunActive}
        sendRun={chatSession.sendRun}
        cancelRun={chatSession.cancelRun}
        confirmRun={chatSession.confirmRun}
        denyRun={chatSession.denyRun}
        respondPrompt={chatSession.respondPrompt}
        retryRun={chatSession.retryRun}
        updateContext={chatSession.updateContext}
        setDraftExplicitContext={chatSession.setDraftExplicitContext}
        confirmDraftExplicitContext={chatSession.confirmDraftExplicitContext}
        clearDraftExplicitContext={chatSession.clearDraftExplicitContext}
        removeExplicitContext={chatSession.removeExplicitContext}
        clearExplicitContext={chatSession.clearExplicitContext}
        loadOlderHistory={chatSession.loadOlderHistory}
        setComposerMode={chatSession.setComposerMode}
        projectUuid={workspace.uuid}
        onActionSettled={chatSession.setMessageActionResult}
      />
    ),
  };
}
